import { ref } from '@vue/composition-api';
import usePeerConnection from '@/compositions/usePeerConnection';
import useRoom from '@/compositions/useRoom';
import useVideo from '@/compositions/useVideo';
import useStreams from '@/compositions/useStreams';
import useSocket from '@/compositions/useSocket';
import useJanusSession from '@/compositions/useJanusSession';
import useConnections from '@/compositions/useConnections';

export default function useJanusListener(
  { janusRoomMemberId, janusRoomPrivateMemberId, janusRoomId }, { emit },
) {
  const { connection, onIceCandidate } = usePeerConnection();
  const { sendEvent, registerMessage } = useSocket();
  const { connections, isVideoDisabled } = useConnections();
  connections.value.push(connection);
  const video = ref(null);
  const audio = ref(null);
  const { room } = useRoom();
  const {
    play, videos, remoteVideos, isVideoMuted,
  } = useVideo();
  const stream = ref(null);
  const { setAudioLevel, getLevel } = useStreams();
  const handleIdRef = ref(null);
  const sessionIdRef = ref(null);
  const { session } = useJanusSession();
  const configured = ref(false);

  function createListener() {
    sendEvent('listener:join', {
      room: room.value,
      janusRoomId,
      janusRoomPrivateMemberId,
      janusRoomMemberId,
      sessionId: session.value.data.id,
    });
  }

  function registerEvents() {
    registerMessage('listener:jsep', async ({
      jsep, handleId, sessionId, janusRoomMemberId: memberId, janusRoomId: roomId,
    }) => {
      if (memberId !== janusRoomMemberId || roomId !== janusRoomId || handleIdRef.value) {
        return;
      }

      handleIdRef.value = handleId;
      sessionIdRef.value = sessionId;

      connection.value.onnegotiationneeded = async (data) => {
        const description = new RTCSessionDescription(jsep);
        await connection.value.setRemoteDescription(description);
        const answer = await connection.value.createAnswer(
          { offerToReceiveAudio: true, offerToReceiveVideo: true },
        );
        answer.sdp = answer.sdp.replaceAll('a=setup:active', 'a=setup:passive');
        await connection.value.setLocalDescription(answer);
        sendEvent('listener:answer', {
          answer,
          handleId,
          sessionId,
          janusRoomId,
        });
      };

      const decription = new RTCSessionDescription(jsep);
      await connection.value.setRemoteDescription(decription);

      const answer = await connection.value.createAnswer(
        { offerToReceiveAudio: true, offerToReceiveVideo: true },
      );
      answer.sdp = answer.sdp.replaceAll('a=setup:active', 'a=setup:passive');

      await connection.value.setLocalDescription(answer);
      sendEvent('listener:answer', {
        answer,
        handleId,
        sessionId,
        janusRoomId,
      });
    });

    connection.value.ontrack = async (mediaStreamEvent) => {
      stream.value = mediaStreamEvent.streams[0];
      if (mediaStreamEvent.track.kind === 'video') {
        video.value.srcObject = stream.value;
        play(video.value);
        videos.value.push({
          video: video.value,
          data: {
            janusRoomMemberId,
          },
        });
        remoteVideos.value.push(mediaStreamEvent.track);
      } else {
        audio.value.srcObject = stream.value;
        play(audio.value);
      }

      videos.value.push({
        video: video.value,
        data: {
          janusRoomMemberId,
        },
      });
      getLevel(stream.value, (val, interval) => {
        setAudioLevel(janusRoomMemberId, parseFloat(val), interval);
      });

      emit('stream', { janusRoomMemberId, stream: stream.value, kind: mediaStreamEvent.track.kind });
    };
  }

  async function joinRoom() {
    registerEvents();
    onIceCandidate((_candidate) => {
      const candidate = _candidate || { completed: true };
      sendEvent('listener:candidate', {
        candidate,
        handleId: handleIdRef.value,
        sessionId: sessionIdRef.value,
        janusRoomId,
      });
    });
    createListener();
  }

  return {
    joinRoom,
    video,
    stream,
    audio,
    connection,
  };
}
